import React, { useState, useEffect } from 'react';
import { ApiKey, ApiKeyUpdateInput } from '../../services/apiKeysService';

interface EditApiKeyModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: ApiKeyUpdateInput) => Promise<void>;
  apiKey: ApiKey | null;
}


const EditApiKeyModal: React.FC<EditApiKeyModalProps> = ({
  isOpen,
  onClose,
  onSave,
  apiKey
}) => {
  const [formData, setFormData] = useState<ApiKeyUpdateInput>({
    key_name: '',
    api_key: '',
    access_type: '',
    rate_limit_per_hour: 100,
    is_active: true,
    expires_at: null
  });
  
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);

  // Populate form when apiKey changes
  useEffect(() => {
    if (apiKey) {
      setFormData({
        key_name: apiKey.key_name,
        api_key: apiKey.api_key,
        access_type: apiKey.access_type,
        rate_limit_per_hour: apiKey.rate_limit_per_hour,
        is_active: apiKey.is_active,
        expires_at: apiKey.expires_at
      });
      setErrors({});
    }
  }, [apiKey]);

  const handleInputChange = (field: keyof ApiKeyUpdateInput, value: string | boolean | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleExpiresAtChange = (value: string) => {
    const dateValue = value ? new Date(value).toISOString() : null;
    setFormData(prev => ({ ...prev, expires_at: dateValue }));
    if (errors.expires_at) {
      setErrors(prev => ({ ...prev, expires_at: '' }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    // Validation
    const newErrors: Record<string, string> = {};

    if (!formData.key_name?.trim()) newErrors.key_name = 'Key name is required';
    if (!formData.api_key?.trim()) newErrors.api_key = 'API key is required';
    // Access type is optional - no validation needed
    
    // Rate limit validation
    if (formData.rate_limit_per_hour && formData.rate_limit_per_hour < 1) newErrors.rate_limit_per_hour = 'Rate limit must be at least 1';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    if (!apiKey) return;

    try {
      setIsSaving(true);
      await onSave(formData);
      onClose();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error('Error updating API key:', errorMessage);
      setErrors({ submit: errorMessage });
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen || !apiKey) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Edit API Key</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 focus:outline-none"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Key Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Key Name *
            </label>
            <input
              type="text"
              value={formData.key_name}
              onChange={(e) => handleInputChange('key_name', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter key name"
            />
            {errors.key_name && (
              <p className="text-red-500 text-xs mt-1">{errors.key_name}</p>
            )}
          </div>

          {/* API Key */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              API Key *
            </label>
            <input
              type="text"
              value={formData.api_key}
              onChange={(e) => handleInputChange('api_key', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter API key"
            />
            {errors.api_key && (
              <p className="text-red-500 text-xs mt-1">{errors.api_key}</p>
            )}
          </div>

          {/* Organisation Name - Read Only Display */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Organization
            </label>
            <div className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-md text-gray-900">
              {apiKey.organisation_name || apiKey.organisation_id}
            </div>
            <p className="text-xs text-gray-500 mt-1">This field cannot be edited</p>
          </div>

          {/* Access Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Access Type
            </label>
            <input
              type="text"
              value={formData.access_type}
              onChange={(e) => handleInputChange('access_type', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter access type (optional)"
            />
            {errors.access_type && (
              <p className="text-red-500 text-xs mt-1">{errors.access_type}</p>
            )}
          </div>

          {/* Rate Limit Per Hour */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Rate Limit Per Hour *
            </label>
            <input
              type="number"
              min="1"
              value={formData.rate_limit_per_hour}
              onChange={(e) => handleInputChange('rate_limit_per_hour', parseInt(e.target.value) || 100)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter rate limit"
            />
            {errors.rate_limit_per_hour && (
              <p className="text-red-500 text-xs mt-1">{errors.rate_limit_per_hour}</p>
            )}
          </div>

          {/* Active Status */}
          <div className="flex items-center space-x-3">
            <label className="text-sm font-medium text-gray-700">Active</label>
            <button
              type="button"
              onClick={() => handleInputChange('is_active', !formData.is_active)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                formData.is_active ? 'bg-blue-600' : 'bg-gray-200'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  formData.is_active ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
            <span className="text-xs text-gray-500">
              {formData.is_active ? 'Active' : 'Inactive'}
            </span>
          </div>

          {/* Expires At */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Expires At
            </label>
            <input
              type="datetime-local"
              value={formData.expires_at ? new Date(formData.expires_at).toISOString().slice(0, 16) : ''}
              onChange={(e) => handleExpiresAtChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p className="text-xs text-gray-500 mt-1">Leave empty for no expiration</p>
            {errors.expires_at && (
              <p className="text-red-500 text-xs mt-1">{errors.expires_at}</p>
            )}
          </div>

          {errors.submit && (
            <div className="text-red-500 text-sm">
              {errors.submit}
            </div>
          )}

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 rounded-md transition-colors"
            >
              Update Data
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditApiKeyModal;
